import {
  Alert,
  AlertIcon,
  Button,
  Center,
  Collapse,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { type IOption, MenuItemLink } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  BankDocumentTypeEnum,
  GetNewSequenceNumberDocument,
  type GetNewSequenceNumberQuery,
  type GetNewSequenceNumberQueryVariables,
  OutgoingInvoiceTypeEnum,
  SequenceTypesEnum,
  useMarkOutgoingInvoiceAsPaidMutation,
} from "@src/__generated__/graphql";
import {
  useDeleteOutgoingInvoiceMutation,
  useGetOutgoingInvoiceSuggestedNumberQuery,
  useUpdateOutgoingInvoiceNumberMutation,
} from "@src/__generated__/urql-graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { DocumentSequenceAutocompleteField } from "@src/components/ui-kit/AutocompleteField/variants/invoice-sequence";
import { Icon } from "@src/components/ui-kit/Icon";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { onError } from "@src/utils/apollo";
import { can, cannot, withCan } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { fieldToInputProps } from "@src/utils/forms/inputHelpers";
import { required } from "@src/utils/forms/validators";
import { useStore } from "@src/utils/hooks";
import { FieldState } from "formstate";
import { action, computed, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { type FunctionComponent, useContext, useEffect } from "react";
import { InvoiceTypeEnum } from "../../../Create/store";
import type { OutgoingInvoiceModel } from "../models";
import { PageOutgoingInvoicesCtx } from "../PageOutgoingInvoices";

export const CREDIT_NOTE_OUTGOING_INVOICE_QUERY_KEY = "outgoingInvoiceId";

export const PopoverRowActions: FunctionComponent<
  React.PropsWithChildren<{ id: string; model: OutgoingInvoiceModel }>
> = withCan(
  {
    do: [
      "outgoingInvoice_sendToClient",
      "outgoingInvoice_update_all",
      "outgoingInvoice_markAsPaid",
      "outgoingInvoice_update_team",
    ],
  },
  observer(function PopoverRowActions({ id, model }) {
    const {
      UIStore,
      sendInvoiceModalStore,
      manualPaymentModalStore,
      matchPaymentModalStore,
      workspaceStore,
    } = useStore();
    const store = useContext(PageOutgoingInvoicesCtx);
    const updateNumberModal = useDisclosure();
    const updateAdvancedOptions = useDisclosure();

    const dp = useDisclosure();
    const deleteModal = useDisclosure();

    const state = useLocalObservable(
      () => ({
        newInvoiceNo: new FieldState(model.invoice_no)
          .validators(required)
          .enableAutoValidation(),
        nextInvoiceNo: new FieldState<string | null>(null),
        fetcher: new FetchHelper<
          GetNewSequenceNumberQuery,
          GetNewSequenceNumberQueryVariables
        >(GetNewSequenceNumberDocument),
        get isLoadingNewSequenceNumber() {
          return this.fetcher.isLoading.value;
        },
        sequenceNumberOptions: [] as IOption[],
        async fetchNewSequenceNumber() {
          if (!model.workspaceCompany?.id) return;

          const [data, error] = await this.fetcher.fetch({
            date: toApiDate(model.date_of_supply),
            workspace_company_id: model.workspaceCompany.id,
            invoice_type: SequenceTypesEnum.OutgoingInvoice,
          });

          if (error) {
            this.sequenceNumberOptions = [];
            return;
          }

          this.sequenceNumberOptions = data.getNewInvoiceNumber.map((item) => ({
            label: item.document_no,
            value: item.id,
          }));
        },
      }),
      {
        isLoadingNewSequenceNumber: computed,
        fetchNewSequenceNumber: action.bound,
        sequenceNumberOptions: observable.ref,
      },
    );

    const [markOutgoingInvoiceAsPaid] = useMarkOutgoingInvoiceAsPaidMutation({
      variables: { id, date: toApiDate(manualPaymentModalStore.date.start) },
      ...onError(),
      onCompleted(res) {
        dp.onClose();
        res.markOutgoingInvoiceAsPaid
          ? store.refetch()
          : UIStore.toast({ title: t`Operation failed`, status: "error" });
      },
    });

    const [{ fetching: fetchingUpdateInvoiceNo }, updateInvoiceNo] =
      useUpdateOutgoingInvoiceNumberMutation();
    const [{ fetching: deletingOi }, deleteOi] =
      useDeleteOutgoingInvoiceMutation();
    const [
      { fetching: suggestedNumberFetching, data: suggestedNumber },
      fetchSuggestedNumber,
    ] = useGetOutgoingInvoiceSuggestedNumberQuery({
      variables: { id },
      requestPolicy: "network-only",
      pause: true,
    });

    useEffect(() => {
      if (!suggestedNumber?.outgoingInvoice?.sequence?.next_invoice_no) return;
      state.nextInvoiceNo.onChange(
        suggestedNumber.outgoingInvoice.sequence.next_invoice_no,
      );
    }, [suggestedNumber?.outgoingInvoice?.sequence?.next_invoice_no]);

    const handleUpdateInvoiceNo = async () => {
      await state.newInvoiceNo.validate();

      if (state.newInvoiceNo.hasError) {
        UIStore.toast({
          status: "warning",
          title: t`New invoice number is required`,
        });
        return;
      }

      const { error } = await updateInvoiceNo({
        input: {
          id: id,
          invoice_no: state.newInvoiceNo.$,
          next_invoice_no: updateAdvancedOptions.isOpen
            ? state.nextInvoiceNo.$
            : null,
        },
      });

      if (error) return;

      store.refetch();
      handleUpdateModalClose();
    };

    const handleUpdateModalClose = () => {
      updateNumberModal.onClose();
      updateAdvancedOptions.onClose();
      state.nextInvoiceNo.onChange(null);
    };

    const handleMatchPayment = async () => {
      store.refetch();
    };

    return (
      <React.Fragment>
        <Menu>
          <MenuButton>
            <Icon name="dots-horizontal" color="#02929d" />
          </MenuButton>
          <MenuList>
            {can("outgoingInvoice_markAsPaid") &&
              (!model.paid_at ? (
                <MenuItem
                  onClick={() => {
                    manualPaymentModalStore.disclosure.onOpen({
                      input: {
                        date: toApiDate(model.due_date),
                        note: model.note ?? "",
                        amount: model.stats.total_amount_with_vat_in_currency,
                        currency_id: model.currency.id,
                      },
                      onUpdate: async () => {
                        await markOutgoingInvoiceAsPaid();
                      },
                    });
                  }}
                >
                  <Trans>Mark as paid manually</Trans>
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() => {
                    store.markAsUnpaidModal.onOpen({
                      id,
                      hasMatchedPayments: (model.accountMoves?.length ?? 0) > 0,
                    });
                  }}
                >
                  <Trans>Mark as unpaid</Trans>
                </MenuItem>
              ))}
            {workspaceStore.settings?.enableBank &&
              can("expense_markAsPaid", "bank_read_all") && (
                <MenuItem
                  onClick={() => {
                    if (!workspaceStore.settings) {
                      UIStore.toast({
                        status: "error",
                      });
                      captureEvent({
                        message:
                          "FE: Workspace store settings failed to initialize",
                      });
                      return;
                    }

                    matchPaymentModalStore.disclosure.onOpen({
                      selectedMoves: model.accountMoves ?? [],
                      document: {
                        id: model.id,
                        type: BankDocumentTypeEnum.OutgoingInvoice,
                        number: model.invoice_no ?? "",
                        amount: model.stats.billing_amount_with_vat,
                        currency: workspaceStore.settings?.currency,
                        exchangeRate: model.exchange_rate,
                      },
                      onUpdate: handleMatchPayment,
                    });
                  }}
                >
                  <Trans>Match payment</Trans>
                </MenuItem>
              )}
            {model.type !== OutgoingInvoiceTypeEnum.ProformaPayment &&
              (can("outgoingInvoice_update_all") ||
                can({
                  do: "outgoingInvoice_update_own",
                  eval: ({ id }) => model.owner.id === id,
                })) && (
                <MenuItemLink
                  href={{
                    pathname: "/invoices/outgoing-invoices/edit",
                    query: { id },
                  }}
                >
                  <Trans>Edit</Trans>
                </MenuItemLink>
              )}
            {model.type !== OutgoingInvoiceTypeEnum.ProformaPayment &&
              can("outgoingInvoice_updateInvoiceNumber") && (
                <MenuItem
                  onClick={() => {
                    updateNumberModal.onOpen();
                    state.newInvoiceNo.onChange(model.invoice_no);
                    state.fetchNewSequenceNumber();
                  }}
                >
                  <Trans>Edit invoice number</Trans>
                </MenuItem>
              )}
            {can("outgoingInvoice_accessAccountingAttributes") && (
              <MenuItem
                onClick={() => {
                  store.changeDatesModalStore.modalState.onOpen({
                    id: model.id,
                    invoice_type: InvoiceTypeEnum.Outgoing,
                    issue_date: model.issue_date,
                    date_of_supply: model.date_of_supply,
                    due_date: model.due_date,
                    paid_at: model.paid_at,
                    onUpdate: () => store.refetch(),
                  });
                }}
              >
                <Trans>Edit dates</Trans>
              </MenuItem>
            )}
            {can("outgoingInvoice_create") && (
              <MenuItemLink
                href={{
                  pathname: "/invoices/outgoing-invoices/duplicate",
                  query: { id },
                }}
              >
                <Trans>Duplicate</Trans>
              </MenuItemLink>
            )}

            <MenuItemLink
              href={{
                pathname: "/invoices/for-invoicing/create-credit-note",
                query: {
                  [CREDIT_NOTE_OUTGOING_INVOICE_QUERY_KEY]: id,
                },
              }}
            >
              <Trans>Create credit note</Trans>
            </MenuItemLink>

            {can("outgoingInvoice_sendToClient") && (
              <MenuItem
                onClick={() => {
                  sendInvoiceModalStore.modalState.onOpen({
                    id: id,
                    number: model.invoice_no,
                    type: "outgoing",
                    onSubmit: () => {
                      sendInvoiceModalStore.modalState.onClose();
                      store.refetch();
                    },
                  });
                }}
              >
                {model.emailLogs.length ? t`Resend` : t`Send`}
              </MenuItem>
            )}

            {model.invoicePdf && (
              <React.Fragment>
                <MenuItemLink isExternal href={model.invoicePdf.preview || ""}>
                  <Trans>Preview</Trans>
                </MenuItemLink>
                <MenuItemLink isExternal href={model.invoicePdf.download || ""}>
                  <Trans>Download</Trans>
                </MenuItemLink>
              </React.Fragment>
            )}
            {model.relatesToProformaPdf && (
              <React.Fragment>
                <MenuItemLink
                  isExternal
                  href={model.relatesToProformaPdf.preview || ""}
                >
                  <Trans>Preview</Trans>
                </MenuItemLink>
                <MenuItemLink
                  isExternal
                  href={model.relatesToProformaPdf.download || ""}
                >
                  <Trans>Download</Trans>
                </MenuItemLink>
              </React.Fragment>
            )}
            <MenuItem
              isDisabled={cannot("outgoingInvoice_delete_all")}
              onClick={() => {
                deleteModal.onOpen();
              }}
            >
              <Trans>Delete</Trans>
            </MenuItem>
          </MenuList>
        </Menu>
        <ModalConfirm
          heading={t`Delete invoice`}
          isOpen={deleteModal.isOpen}
          isLoading={deletingOi}
          destructive
          onClose={deleteModal.onClose}
          onConfirm={async () => {
            try {
              await deleteOi({ id: id });
              deleteModal.onClose();
              store.refetch();
            } catch {}
          }}
        >
          <Text fontWeight="bold">
            <Trans>
              Removing this invoice will alter the number sequence and impact
              financial reports.
            </Trans>
          </Text>
          {!!model.projectTimeBilling?.id && (
            <Alert mt="4" status="warning">
              <AlertIcon />
              <Text>
                <Trans>
                  This invoice is linked to a locked Time Billing. Please update
                  the Time Billing after deleting.
                </Trans>
              </Text>
            </Alert>
          )}
        </ModalConfirm>
        <Modal
          isOpen={updateNumberModal.isOpen}
          onClose={handleUpdateModalClose}
          trapFocus={false}
        >
          <ModalOverlay />
          <ModalContent
            minW={{
              base: "full",
              md: "500px",
            }}
          >
            <ModalHeader>
              <Trans>Edit invoice number</Trans>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <DocumentSequenceAutocompleteField
                value={state.newInvoiceNo.value}
                onChange={state.newInvoiceNo.onChange}
                autoCompleteOptions={state.sequenceNumberOptions}
                onPopoverOpen={state.fetchNewSequenceNumber}
                onSelect={(sequenceNumber) => {
                  state.newInvoiceNo.onChange(sequenceNumber.label);
                }}
                isLoading={state.isLoadingNewSequenceNumber}
                error={state.newInvoiceNo.error}
                label={t`Invoice number`}
                placeholder={t`Invoice number`}
              />

              <Button
                onClick={() => {
                  updateAdvancedOptions.onToggle();
                  fetchSuggestedNumber();
                }}
                size="sm"
                variant="link"
              >
                <Trans>Show advanced options</Trans>
              </Button>
              <Collapse in={updateAdvancedOptions.isOpen}>
                {suggestedNumberFetching ? (
                  <Center minH="14">
                    <Spinner />
                  </Center>
                ) : (
                  <Stack
                    align={{ base: "start", md: "center" }}
                    justify="flex-end"
                    flexDir={{ base: "column", md: "row" }}
                    gap={{ base: "0", md: "2" }}
                    mt="2"
                  >
                    <Text>
                      <Trans>Number to be used on next created invoice:</Trans>
                    </Text>
                    <Input
                      w={{
                        base: "full",
                        md: "140px",
                      }}
                      {...fieldToInputProps(
                        state.nextInvoiceNo,
                        ({ target }) => target.value,
                      )}
                      placeholder={t`Next invoice number`}
                    />
                  </Stack>
                )}
              </Collapse>
            </ModalBody>
            <ModalFooter gap="2">
              <Button
                colorScheme="grey"
                onClick={updateNumberModal.onClose}
                variant="outline"
              >
                <Trans>Close</Trans>
              </Button>
              <Button
                isLoading={fetchingUpdateInvoiceNo}
                onClick={handleUpdateInvoiceNo}
              >
                <Trans>Save</Trans>
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </React.Fragment>
    );
  }),
);
